import { __awaiter, __generator, __spreadArrays } from "tslib";
/**
 * @file http 请求类
 * @author mrlyj<mr.lyj@outlook.com>
 */
import axios from 'axios';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from '../utils/compose';
import createError from './createError';
import RequestQueue from './RequestQueue';
import { channelControl, setChannleInUserInfoApi, channelKey } from './channelControl';
/**
 * 基于 axios 封装的请求类
 *
 * support feature
 * - 请求缓存
 * - 重复请求过滤
 * - interceptors middleware（多拦截器中间件）
 * - 异常错误处理
 *  - errorCode 9999 errorMessage 'http link failed'（包含所有未能正常返回的http status）
 *  - errorCode 9998 errorMessage 'timeout request'
 *  - errorCode 3040 errorMessage 'read cache'
 *  - errorCode 2040 errorMessage 'repeat request'
 * - 请求队列start和end callback
 *
 * @todo
 * plan
 * - SWR（http协议 stale-while-revalidate 缓存功能）
 * - loading delay
 *
 * depend
 * - axios 0.18.0 暂不支持过高的版本
 *
 * issue
 * - baseURL 绝对地址和相对地址时，会导致缓存失效 fix
 *
 * @example
 * const client = new HttpClient({}) // 实例http请求对象
 * // 请求/api/todos 返回Promise对象
 * client.createHttp("get", "/api/todos")
 * // 请求/api/todos 返回Promise对象，使用localStorage缓存请求
 * client.createHttp("get", "/api/todos", {
 *  cache: 'local'
 * })
 *
 * // 请求/api/todos 返回Promise对象，开启请求缓存，自定义存取器，适用于Hybird模式开发
 * const client = new HttpClient({}, {
 *  store: {
 *    getData: function (key) { return ...读取数据... },
 *    setData: function (key, data) {
 *      ...保存数据...
 *    },
 *  }
 * })
 * client.createHttp("get", "/api/todos", {
 *  cache: 'local',
 * })
 *
 * @version 0.0.1
 * @class
 */
var HttpClient = /** @class */ (function () {
    /**
     * @constructor
     * @param {AxiosRequestConfig} config axios 请求配置
     * @param {object} [options] 扩展选项
     * @param {string} [options.cacheKey=http-cache] 缓存key
     * @param {(Store|boolean)} [options.store=false] 存取器
     * @param {Config} [options.config] 配置
     * @param {RequestOption} [options.request] 请求
     * @param {ResponseOption} [options.response] 响应
     * @param {function} [options.load] 请求 load 操作
     */
    function HttpClient(config, options) {
        var _this_1 = this;
        if (options === void 0) {
            options = {};
        }
        var _this = this;
        options = defaultsDeep(options, {
            cacheKey: 'http-cache',
            store: false,
            load: {},
            config: {
                channel: false,
                channelRoutes: [],
            },
            request: { interceptors: [] },
            response: { interceptors: [] },
        });
        this._queue = new RequestQueue();
        Object.keys(options.load).forEach(function (name) {
            _this._queue.on(name, options.load[name]);
        });
        /**
         * 实例传参配置副本
         */
        this._config = options.config;
        /**
         * 基础信息
         */
        this._base = window.sessionStorage.getItem(channelKey)
            ? JSON.parse(window.sessionStorage.getItem(channelKey))
            : {
                default_channel: '',
                use_channel: '',
                channel_table: {},
            };
        /**
         * 定义缓存级别
         * @readonly
         * @type {string[]}
         * @default ['', 'memory', 'session', 'local']
         */
        this._CACHE_LEVEL = ['', 'memory', 'session', 'local'];
        /**
         * 缓存key
         * @type {string}
         */
        this.cacheKey = options.cacheKey;
        /**
         * axios实例对象
         * @protected
         * @type {AxiosInstance}
         */
        this.axiosInstance = axios.create(config);
        /**
         * 存取器
         * @type {Store}
         */
        this.store = options.store;
        /**
         * 内存缓存对象
         * @type {object}
         */
        this.memoryData = {};
        /**
         * 请求拦截中间件
         * @type {requestOption}
         */
        this.requestInterceptors = {
            interceptors: __spreadArrays(options.request.interceptors.map(function (item) { return item.interceptor; })),
            errors: __spreadArrays(options.request.interceptors.map(function (item) { return item.error; })),
        };
        /**
         * 响应拦截中间件
         * @type {responseOption}
         */
        this.responseMiddlewares = {
            interceptors: __spreadArrays(options.response.interceptors.map(function (item) { return item.interceptor; })),
            errors: __spreadArrays(options.response.interceptors.map(function (item) { return item.error; })),
        };
        // 重写内部 axios 实例的响应拦截对象 use 方法
        var resUse = this.axiosInstance.interceptors.response.use;
        this.axiosInstance.interceptors.response.use = function (interceptor, errorHandler) {
            resUse.call(_this_1.axiosInstance.interceptors.response, function (response) {
                var middlewares = __spreadArrays(_this_1.responseMiddlewares.interceptors, [
                    _this_1._cachePipe,
                    _this_1._responsePipe,
                ]);
                return interceptor(compose.apply(void 0, middlewares)(response));
            }, function (error) {
                var middlewares = __spreadArrays(_this_1.responseMiddlewares.errors, [
                    _this_1._responseErrorPipe,
                    _this_1._responsePipe,
                ]);
                return errorHandler(compose.apply(void 0, middlewares)(error));
            });
        };
        // 运行拦截器
        this.axiosInstance.interceptors.response.use(function (response) { return response; }, function (error) { return error; });
        // 重写内部 axios 实例的请求拦截对象 use 方法
        var reqUse = this.axiosInstance.interceptors.request.use;
        this.axiosInstance.interceptors.request.use = function (interceptor, errorHandler) {
            reqUse.call(_this_1.axiosInstance.interceptors.request, function (config) {
                var middlewares = __spreadArrays(_this_1.requestInterceptors.interceptors, [
                    _this_1._cancelPipe,
                    _this_1._enhanceRequestPipe,
                ]);
                return interceptor(compose.apply(void 0, middlewares)(config));
            }, errorHandler);
        };
        // 运行拦截器
        this.axiosInstance.interceptors.request.use(function (config) { return config; }, function (error) { return error; });
        // bind this
        this._enhanceRequestPipe = this._enhanceRequestPipe.bind(this);
        this._clearCache = this._clearCache.bind(this);
        this._cancelPipe = this._cancelPipe.bind(this);
        this._cachePipe = this._cachePipe.bind(this);
        this._responsePipe = this._responsePipe.bind(this);
        this._responseErrorPipe = this._responseErrorPipe.bind(this);
    }
    /**
     * 发起请求
     * @method
     * @param {string} method=GET 请求方式
     * @param {string} url 请求地址
     * @param {object} [param]
     * @param {Method} param.method axios请求方式
     * @param {AxiosRequestConfig} param.config axios请求配置
     * @param {object} param.data 请求入惨
     * @param {string} param.cache 缓存级别
     * @param {boolean} param.forceRequest 请求入惨
     * @param {boolean} param.loading
     */
    HttpClient.prototype.createHttp = function (method, url, options) {
        if (method === void 0) {
            method = 'GET';
        }
        if (options === void 0) {
            options = {};
        }
        return __awaiter(this, void 0, void 0, function () {
            var cancel, token, params, result;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        options = Object.assign({}, {
                            config: {},
                            data: {},
                            cache: '',
                            forceRequest: false,
                            loading: true,
                        }, options);
                        if (!this._CACHE_LEVEL.includes(options.cache)) {
                            throw new Error('Cache Parameter Error');
                        }
                        if (this.store && options.cache && options.cache !== this._CACHE_LEVEL[3]) {
                            throw new Error('Cache Parameter Must Set Local, Because you used a custom store, request url: ' + url);
                        }
                        token = new axios.CancelToken(function (_cancel) {
                            cancel = _cancel;
                        });
                        params = Object.assign({}, {
                            method: method,
                            data: options.data,
                            url: url,
                            status: {
                                path: url,
                            },
                        }, options.config, {
                            cancelToken: token,
                            cache: options.cache,
                            cancel: cancel,
                            forceRequest: options.forceRequest,
                            loading: options.loading,
                        });
                        if (options.cache) {
                            params.clearCache = this._clearCache(params);
                        }
                        return [4 /*yield*/, this.axiosInstance.request(params)];
                    case 1:
                        result = _a.sent();
                        return [2 /*return*/, result];
                }
            });
        });
    };
    /**
     * 清除缓存功能
     * @param {*} options
     */
    HttpClient.prototype._clearCache = function (config) {
        var key = this._getCacheKey(config);
        var _this = this;
        return function () {
            switch (config.cache) {
                case 'memory':
                    delete _this.memoryData[key];
                    break;
                case 'session':
                    sessionStorage.removeItem(key);
                    break;
                case 'local':
                    _this.store ? _this.store.clearData(key) : localStorage.removeItem(key);
                    break;
                default:
                    break;
            }
        };
    };
    /**
     * 获取请求标示
     * @param {EnhanceAxiosRequestConfig} config axios 请求配置
     * @returns {string} 请求标示
     */
    HttpClient.prototype._getRequestKey = function (config) {
        if (config === void 0) {
            config = {};
        }
        // return config.method + '-' + config.status.path;
        return config.method + '-' + config.status ? config.status.path ? config.status.path : "/EMP/getAllCity" : "/EMP/getAllCity";
    };
    /**
     * 获取规定缓存 key
     * @method
     * @param {EnhanceAxiosRequestConfig} config
     */
    HttpClient.prototype._getCacheKey = function (config) {
        return this.cacheKey + '-' + config.url.replace(config.baseURL, '');
    };
    /**
     * 缓存数据
     * @method
     * @param {AxiosResponse} response axios 响应内容
     * @returns {AxiosResponse}
     */
    HttpClient.prototype._cachePipe = function (response) {
        var status = response.status, config = response.config;
        if (status === 200) {
            var key = this._getCacheKey(config);
            switch (config.cache) {
                case 'memory':
                    this.memoryData[key] = response.data;
                    break;
                case 'session':
                    sessionStorage.setItem(key, JSON.stringify(response.data));
                    break;
                case 'local':
                    this.store
                        ? this.store.setData(key, response.data)
                        : localStorage.setItem(key, JSON.stringify(response.data));
                    break;
                default:
                    break;
            }
        }
        return response;
    };
    /**
     * 扩展请求信息
     * @param {EnhanceAxiosRequestConfig} config axios 配置
     * @returns {EnhanceAxiosRequestConfig}
     */
    HttpClient.prototype._enhanceRequestPipe = function (config) {
        var config = channelControl.call(this, config);
        config._channel = this._base.use_channel;
        return config;
    };
    /**
     * 读取缓存，取消请求
     * @method
     * @param {EnhanceAxiosRequestConfig} config axios 配置
     * @returns {EnhanceAxiosRequestConfig}
     */
    HttpClient.prototype._cancelPipe = function (config) {
        // 判断是否强制刚更新缓存，忽略缓存后续操作
        var atomicService = false;
        if (window.CSNativeObject && window.CSNativeObject.getOSType) {
            var osType = window.CSNativeObject.getOSType().toLowerCase();
            atomicService = osType === 'harmony_atomicservice';
        }
        if (config.forceRequest)
            return config;
        var cache = config.cache, cancel = config.cancel;
        var key = this._getCacheKey(config);
        var reqKey = this._getRequestKey(config);
        var reqs = this._queue.filter(reqKey);
        if (atomicService && key.includes('getAllCity'))
            return config;
        if (reqs.length !== 0) {
            reqs.forEach(function (item) {
                item.cancel({
                    cancelMark: 'repeat',
                    config: config,
                });
            });
        }
        else {
            switch (cache) {
                case 'memory':
                    if (this.memoryData[key]) {
                        cancel({
                            cancelMark: 'cache',
                            config: config,
                            cacheData: this.memoryData[key],
                        });
                    }
                    break;
                case 'session':
                    if (sessionStorage.getItem(key)) {
                        cancel({
                            cancelMark: 'cache',
                            config: config,
                            cacheData: JSON.parse(sessionStorage.getItem(key)),
                        });
                    }
                    break;
                case 'local':
                    if (this.store) {
                        var c = this.store.getData(key);
                        if (c)
                            cancel({
                                cancelMark: 'cache',
                                config: config,
                                cacheData: c,
                            });
                    }
                    else {
                        var c = localStorage.getItem(key);
                        if (c)
                            cancel({
                                cancelMark: 'cache',
                                config: config,
                                cacheData: JSON.parse(c),
                            });
                    }
                    break;
                default:
                    break;
            }
        }
        this._queue.add(reqKey, config);
        return config;
    };
    /**
     * 响应报错格式
     * @param {AxiosError} axios 报错信息
     * @returns {EnhanceAxiosError}
     */
    HttpClient.prototype._responseErrorPipe = function (error) {
        //拦截请求，返回缓存数据
        if (axios.isCancel(error)) {
            switch (error.message.cancelMark) {
                case 'cache':
                    return createError(error.message, 3040, '', 'read cache');
                case 'repeat':
                    return createError(error.message, 2040, '', 'repeat request');
                default:
                    break;
            }
        }
        if (error.code && error.code === 'ECONNABORTED') {
            return createError(error, 9998, '', 'timeout request');
        }
        return createError(error);
    };
    /**
     * 相应内容处理
     * @param {*} response
     */
    HttpClient.prototype._responsePipe = function (response) {
        setChannleInUserInfoApi.call(this, response);
        var key = this._getRequestKey(axios.isCancel(response) ? response.message.config : response.config);
        this._queue.remove(key);
        return response;
    };
    return HttpClient;
}());
export default HttpClient;
/**
 * axios 错误
 * @typedef {object} AxiosError
 * @property {AxiosRequestConfig} config
 * @property {string} code
 * @property {*} request
 * @property {AxiosResponse} response
 * @property {boolean} isAxiosError
 * @property {function} toJSON
 */
/**
 * 配置
 * @typedef {object} Config
 * @property {boolean} channel false
 */
/**
 * 存取器
 * @typedef {object} Store
 * @property {function} getData 读取数据
 * @property {function} setData 保存数据
 * @property {function} clearData 清除数据
 */
/**
 * 请求选项
 * @typedef {object} RequestOption
 * @property {(array|Interceptor)} interceptors 拦截器
 */
/**
 * 响应选项
 * @typedef {object} ResponseOption
 * @property {(array|Interceptor)} interceptors 拦截器
 */
/**
 * 拦截器
 * @typeof {object} Interceptor
 * @property {object} interceptor 拦截方法
 * @property {object} error 报错方法
 */
/**
 * 增强的 axios 配置
 * @typedef {object} EnhanceAxiosRequestConfig
 * @extends AxiosRequestConfig
 * @property {string} cache 缓存标示
 * @property {boolean} forceRequest 强制请求，忽略缓存功能
 * @property {object} data 请求数据
 * @property {AxiosRequestConfig} config axios配置，优先级高于data其它选项
 * @property {number} loadingDelay loading延迟时间（暂未开发）
 * @property {(boolean|string)} [loadStatus='loading']
 */
/**
 * 错误信息
 * @typedef {object} EnhanceAxiosError
 * @extends AxiosError
 * @property {string} errorCode 错误码
 * @property {string} errorMessage 错误信息
 * @property {string | object} errorData 错误数据
 */



//////////////////
// WEBPACK FOOTER
// ../emp-helpers/dist/es/request/HttpClient.js
// module id = null
// module chunks = 